import React, { useMemo, useState } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";

import { Avatar } from "../../components/Avatar";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { CustomSelector } from "../../components/CustomSelector";
import { UnifiedModal } from "../../components/UnifiedModal";
import { useTheme as useCustomTheme } from "../../theme/useTheme";
import { platformColors } from "../../theme/theme";
import { CONTENT_STATUS_BADGES, getContentStatusTone } from "../../theme/badgeTones";
import { PLATFORM_META } from "./workflow/platformMeta";

// Helper functions
const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfMonth = (year, month) => {
    const day = new Date(year, month, 1).getDay();
    return (day + 6) % 7;
};

const formatMonthYear = (date) => {
    const month = date.toLocaleDateString("es-ES", { month: "long" });
    const monthLabel = `${month.charAt(0).toUpperCase()}${month.slice(1)}`;
    return `${monthLabel} de ${date.getFullYear()}`;
};

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const HOURS_IN_DAY = Array.from({ length: 24 }, (_, index) => index);
const HOUR_ROW_HEIGHT = 40;
const TIME_COLUMN_WIDTH = 64;
const EVENT_DURATION_MINUTES = 30;

const formatWeekdayLabel = (date) => {
    const weekday = date
        .toLocaleDateString("es-ES", { weekday: "short" })
        .toUpperCase()
        .replace(".", "");
    const day = date.toLocaleDateString("es-ES", { day: "2-digit" });
    return `${weekday} ${day}`;
};

const getStartOfWeek = (date) => {
    const dayIndex = (date.getDay() + 6) % 7;
    const start = new Date(date);
    start.setDate(date.getDate() - dayIndex);
    start.setHours(0, 0, 0, 0);
    return start;
};

const getDateKey = (date) => {
    const pad = (value) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const VIEW_OPTIONS = [
    { value: "week", label: "Semanal" },
    { value: "month", label: "Mensual" },
];

const getPlatformIcon = (platform) => PLATFORM_META[platform?.toLowerCase()]?.Icon || null;


// Publication status of the network (bundle.social) wins over the approval status.
const publicationStatus = (socialPost) => {
    if (!socialPost) return null;
    if (socialPost.status === "published") return "published";
    if (socialPost.status === "error") return "error";
    if (socialPost.status === "scheduled" || socialPost.status === "review") return "scheduled";
    return null;
};

const formatTooltipDate = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

// One card per piece and publish time: errors win, then pending, then published.
const PUBLICATION_PRIORITY = ["error", "scheduled", "published"];
const getGroupStatus = (statuses) =>
    PUBLICATION_PRIORITY.find((status) => statuses.includes(status)) || statuses[0];

function ClientAvatar({ event, size = 18 }) {
    return (
        <Avatar
            name={event.clientName || "Cliente"}
            src={event.clientLogo || undefined}
            sx={{ width: size, height: size, fontSize: size * 0.45, flexShrink: 0 }}
        />
    );
}

function PlatformIcons({ platforms, size = 16 }) {
    const { colors } = useCustomTheme();
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, overflow: "hidden" }}>
            {platforms.map((platform) => {
                const PlatformIcon = getPlatformIcon(platform);
                const platformColor = platformColors[platform?.toLowerCase()] || colors.primary.main;
                return PlatformIcon ? (
                    <PlatformIcon key={platform} sx={{ fontSize: size, color: platformColor, flexShrink: 0 }} />
                ) : (
                    <Box
                        key={platform}
                        sx={{
                            width: size,
                            height: size,
                            borderRadius: "4px",
                            backgroundColor: platformColor,
                            flexShrink: 0,
                        }}
                    />
                );
            })}
        </Box>
    );
}

const getEventStartMinutes = (date) => date.getHours() * 60 + date.getMinutes();

const getEventLayout = (events) => {
    if (!events.length) return [];

    const sorted = [...events].sort(
        (a, b) => a.scheduledDate - b.scheduledDate
    );
    const active = [];
    const assigned = [];

    sorted.forEach((event) => {
        const startMinutes = getEventStartMinutes(event.scheduledDate);
        const endMinutes = startMinutes + EVENT_DURATION_MINUTES;

        for (let index = active.length - 1; index >= 0; index -= 1) {
            if (active[index].endMinutes <= startMinutes) {
                active.splice(index, 1);
            }
        }

        const usedColumns = new Set(active.map((item) => item.columnIndex));
        let columnIndex = 0;
        while (usedColumns.has(columnIndex)) {
            columnIndex += 1;
        }

        const entry = {
            ...event,
            startMinutes,
            endMinutes,
            columnIndex,
            columnCount: 1,
        };
        active.push(entry);
        assigned.push(entry);
    });

    assigned.forEach((event) => {
        const overlap = assigned.filter(
            (other) =>
                other.startMinutes < event.endMinutes &&
                event.startMinutes < other.endMinutes
        );
        event.columnCount = Math.max(
            ...overlap.map((item) => item.columnIndex + 1)
        );
    });

    return assigned;
};

// Status colors: same tones as the status badges of the content tables.
const getStatusStyles = (status) => {
    const tone = getContentStatusTone(status);
    return { background: tone.background, text: tone.color, border: tone.background };
};

// Only publications carry a date now; anything else is a legacy date and is painted grey (muted tone).
const LEGACY_STATUS = "legacy";
const LEGEND_STATUSES = ["scheduled", "published", "error"];

// Legend with the publication states shown in the calendar.
function StatusLegend({ swatchSize = 12, sx }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "wrap",
                ...sx,
            }}
        >
            {LEGEND_STATUSES.map((status) => [status, CONTENT_STATUS_BADGES[status]]).map(([status, { label }]) => (
                <Box key={status} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                        sx={{
                            width: swatchSize,
                            height: swatchSize,
                            borderRadius: "3px",
                            backgroundColor: getStatusStyles(status).text,
                        }}
                    />
                    <Typography variant="caption" color="text.secondary">
                        {label}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

// Day cell component
function DayCell({
    day,
    isCurrentMonth,
    isToday,
    isWeekend,
    events,
    onClick,
    isLastCol,
    isLastRow,
}) {
    const { theme, colors, neutrals } = useCustomTheme();

    return (
        <Box
            onClick={() => onClick(day)}
            sx={{
                position: "relative",
                height: "100%",
                minHeight: 0,
                padding: "6px 6px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                backgroundColor: isCurrentMonth
                    ? isWeekend
                        ? neutrals.gray[50]
                        : neutrals.white
                    : neutrals.gray[50],
                borderRadius: 0,
                borderRight: isLastCol ? "none" : `1px solid ${neutrals.gray[200]}`,
                borderBottom: isLastRow ? "none" : `1px solid ${neutrals.gray[200]}`,
                boxShadow: isToday ? `inset 0 0 0 2px ${colors.primary.main}` : "none",
                boxSizing: "border-box",
                overflow: "hidden",
                transition: "all 0.2s ease",
                "&:hover .day-number": {
                    color: colors.primary.main,
                },
                "&:hover .day-number-badge": {
                    backgroundColor: `${colors.primary.main}1A`,
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                <Box
                    className="day-number-badge"
                    sx={{
                        minWidth: 24,
                        height: 24,
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: isToday ? `${colors.primary.main}1A` : "transparent",
                    }}
                >
                    <Typography
                        variant="body2"
                        className="day-number"
                        sx={{
                            fontWeight: isToday ? 700 : 600,
                            color: isCurrentMonth
                                ? isToday
                                    ? colors.primary.main
                                    : theme.palette.text.primary
                                : neutrals.gray[300],
                        }}
                    >
                        {day}
                    </Typography>
                </Box>
            </Box>

            {/* Event indicators */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    mt: 0.5,
                    overflow: "hidden",
                    flex: 1,
                    minHeight: 0,
                    pointerEvents: "none",
                }}
            >
                {events.slice(0, 3).map((event) => {
                    const statusStyles = getStatusStyles(event.status);

                    return (
                        <Box
                            key={event.id}
                            sx={{
                                backgroundColor: statusStyles.background,
                                borderRadius: "6px",
                                padding: "3px 8px",
                                border: `0.5px solid ${statusStyles.border}`,
                                overflow: "hidden",
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    color: statusStyles.text,
                                    fontSize: "0.7rem",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "block",
                                }}
                            >
                                {event.title}
                            </Typography>
                        </Box>
                    );
                })}
                {events.length > 3 && (
                    <Typography
                        variant="caption"
                        sx={{ color: neutrals.gray[500], fontSize: "0.7rem" }}
                    >
                        +{events.length - 3} más
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

export function ContentCalendar({
    clientId,
    platformAdaptations = [],
    onContentClick,
    onScheduleClick,
    showClientName = false,
    clientNameById = {},
    clientNameByConfigId = {},
    clientLogoByConfigId = {},
    currentDate: currentDateProp,
    onCurrentDateChange,
    calendarView: calendarViewProp,
    onCalendarViewChange,
    showHeader = true,
}) {
    const { theme, colors, neutrals } = useCustomTheme();
    const [internalCurrentDate, setInternalCurrentDate] = useState(new Date());
    const [internalCalendarView, setInternalCalendarView] = useState("week");
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const currentDate = currentDateProp ?? internalCurrentDate;
    const calendarView = calendarViewProp ?? internalCalendarView;
    const setCurrentDate = onCurrentDateChange ?? setInternalCurrentDate;
    const setCalendarView = onCalendarViewChange ?? setInternalCalendarView;

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const today = new Date();

    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);
    const daysInPrevMonth = getDaysInMonth(year, month - 1);
    const totalRows = Math.ceil((firstDayOfMonth + daysInMonth) / 7);
    const weekStart = getStartOfWeek(currentDate);
    const weekDays = useMemo(() => {
        return Array.from({ length: 7 }, (_, index) => {
            const day = new Date(weekStart);
            day.setDate(weekStart.getDate() + index);
            return day;
        });
    }, [weekStart]);

    // Navigation handlers
    const goToPreviousMonth = () => {
        setCurrentDate(new Date(year, month - 1, 1));
    };

    const goToNextMonth = () => {
        setCurrentDate(new Date(year, month + 1, 1));
    };

    const goToPreviousWeek = () => {
        const previousWeek = new Date(currentDate);
        previousWeek.setDate(currentDate.getDate() - 7);
        setCurrentDate(previousWeek);
    };

    const goToNextWeek = () => {
        const nextWeek = new Date(currentDate);
        nextWeek.setDate(currentDate.getDate() + 7);
        setCurrentDate(nextWeek);
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    const formattedSelectedDate = useMemo(() => {
        if (!selectedDate) return "";
        return selectedDate.toLocaleDateString("es-ES", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    }, [selectedDate]);

    // Transform platform adaptations to calendar events: one per piece and publish time
    const scheduledEvents = useMemo(() => {
        const groups = new Map();
        platformAdaptations.forEach((adaptation) => {
            if (!adaptation.scheduled_at) return;
            const scheduledDate = new Date(adaptation.scheduled_at);
            if (Number.isNaN(scheduledDate.getTime())) return;

            const pieceId = adaptation.content_piece_id ?? adaptation.content_piece;
            const configId =
                adaptation.config_id ??
                adaptation.content_piece_config_id ??
                adaptation.config;
            const pieceStatus =
                adaptation.content_piece_status ?? adaptation.status ?? "draft";
            const pieceTitle =
                adaptation.content_piece_title ||
                adaptation.title ||
                `Contenido #${pieceId || adaptation.id}`;
            // Only network publications get a colour; a legacy date without one falls back to grey.
            const status = publicationStatus(adaptation.social_post) || LEGACY_STATUS;
            const platform = adaptation.platform || "content";

            const key = `${pieceId ?? `adaptation-${adaptation.id}`}-${scheduledDate.getTime()}`;
            const existing = groups.get(key);
            if (existing) {
                existing.platforms.push(platform);
                existing.statuses.push(status);
                return;
            }
            groups.set(key, {
                id: key,
                adaptationId: adaptation.id,
                dateKey: getDateKey(scheduledDate),
                scheduledDate,
                title: pieceTitle,
                platforms: [platform],
                statuses: [status],
                clientName:
                    adaptation.client_name ||
                    (configId && clientNameByConfigId[configId]) ||
                    (adaptation.client_id && clientNameById[adaptation.client_id]),
                clientLogo: configId ? clientLogoByConfigId[configId] : null,
                piece: {
                    id: pieceId,
                    config: configId,
                    status: pieceStatus,
                    title: pieceTitle,
                    client_name: adaptation.client_name,
                    agency_name: adaptation.agency_name,
                },
            });
        });
        return Array.from(groups.values(), ({ statuses, ...event }) => ({
            ...event,
            status: getGroupStatus(statuses),
        }));
    }, [platformAdaptations, clientNameByConfigId, clientNameById, clientLogoByConfigId]);

    const getEventsForDate = (day, monthOffset = 0) => {
        const targetDate = new Date(year, month + monthOffset, day);
        const targetKey = getDateKey(targetDate);
        return scheduledEvents.filter((event) => event.dateKey === targetKey);
    };

    // Handle day click
    const handleDayClick = (day, monthOffset = 0) => {
        const clickedDate = new Date(year, month + monthOffset, day);
        setSelectedDate(clickedDate);
        setSelectedEvents(getEventsForDate(day, monthOffset));
        setIsModalOpen(true);
    };

    // Build calendar grid
    const buildCalendarGrid = () => {
        const grid = [];

        // Previous month days
        for (let i = firstDayOfMonth - 1; i >= 0; i--) {
            const day = daysInPrevMonth - i;
            grid.push({
                day,
                isCurrentMonth: false,
                events: getEventsForDate(day, -1),
                monthOffset: -1,
            });
        }

        // Current month days
        for (let day = 1; day <= daysInMonth; day++) {
            const isToday =
                day === today.getDate() &&
                month === today.getMonth() &&
                year === today.getFullYear();

            grid.push({
                day,
                isCurrentMonth: true,
                isToday,
                events: getEventsForDate(day),
                monthOffset: 0,
            });
        }

        // Next month days
        const remainingDays = totalRows * 7 - grid.length;
        for (let day = 1; day <= remainingDays; day++) {
            grid.push({
                day,
                isCurrentMonth: false,
                events: getEventsForDate(day, 1),
                monthOffset: 1,
            });
        }

        return grid;
    };

    const calendarGrid = buildCalendarGrid();

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
                backgroundColor: neutrals.white,
            }}
        >
            {/* Calendar Header */}
            {showHeader && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 3,
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={goToToday}
                            sx={{
                                paddingInline: "0px",
                                borderColor: neutrals.gray[300],
                                fontWeight: 600,
                            }}
                        >
                            Hoy
                        </Button>

                        {/* Navigation arrows */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                            <IconButton
                                onClick={calendarView === "month" ? goToPreviousMonth : goToPreviousWeek}
                                size="small"
                                sx={{
                                    width: 32,
                                    height: 32,
                                    color: neutrals.gray[700],
                                    "&:hover": {
                                        backgroundColor: neutrals.gray[100],
                                    },
                                }}
                            >
                                <ChevronLeft size={18} />
                            </IconButton>
                            <IconButton
                                onClick={calendarView === "month" ? goToNextMonth : goToNextWeek}
                                size="small"
                                sx={{
                                    width: 32,
                                    height: 32,
                                    color: neutrals.gray[700],
                                    "&:hover": {
                                        backgroundColor: neutrals.gray[100],
                                    },
                                }}
                            >
                                <ChevronRight size={18} />
                            </IconButton>
                        </Box>

                        {/* Month/Year */}
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                fontSize: "1.2rem",
                                display: "inline-flex",
                                justifyContent: "flex-start",
                                flexShrink: 0,
                            }}
                        >
                            {calendarView === "month"
                                ? formatMonthYear(currentDate)
                                : formatMonthYear(weekStart)}
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ minWidth: 160 }}>
                            <CustomSelector
                                value={calendarView}
                                onChange={(value) => setCalendarView(value)}
                                options={VIEW_OPTIONS}
                                placeholder="Vista"
                            />
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Calendar grid */}
            {calendarView === "month" ? (
                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                        scrollbarGutter: "stable",
                        "&::-webkit-scrollbar": {
                            width: "6px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: neutrals.gray[300],
                            borderRadius: "3px",
                        },
                        border: `1px solid ${neutrals.gray[200]}`,
                        backgroundColor: neutrals.white,
                    }}
                >
                    {/* Weekday headers */}
                    <Box
                        sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            backgroundColor: neutrals.white,
                            borderBottom: `1px solid ${neutrals.gray[200]}`,
                        }}
                    >
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(7, 1fr)",
                                gap: 0,
                            }}
                        >
                            {WEEKDAYS.map((day) => (
                                <Box
                                    key={day}
                                    sx={{
                                        textAlign: "center",
                                        padding: "6px 0",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 600,
                                            color: neutrals.gray[500],
                                            textTransform: "uppercase",
                                            letterSpacing: "0.04em",
                                        }}
                                    >
                                        {day}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(7, 1fr)",
                            gap: 0,
                            gridAutoRows: "130px",
                            alignItems: "stretch",
                        }}
                    >
                        {calendarGrid.map((cell, index) => {
                            const col = index % 7;
                            const row = Math.floor(index / 7);
                            const isWeekend = col === 5 || col === 6;

                            return (
                                <DayCell
                                    key={index}
                                    day={cell.day}
                                    isCurrentMonth={cell.isCurrentMonth}
                                    isToday={cell.isToday}
                                    isWeekend={isWeekend}
                                    events={cell.events}
                                    onClick={(day) => handleDayClick(day, cell.monthOffset)}
                                    isLastCol={col === 6}
                                    isLastRow={row === totalRows - 1}
                                />
                            );
                        })}
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        border: `1px solid ${neutrals.gray[200]}`,
                        backgroundColor: neutrals.white,
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: "auto",
                            scrollbarGutter: "stable",
                            "&::-webkit-scrollbar": {
                                width: "6px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: neutrals.gray[300],
                                borderRadius: "3px",
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: `${TIME_COLUMN_WIDTH}px repeat(7, minmax(0, 1fr))`,
                                borderBottom: `1px solid ${neutrals.gray[200]}`,
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                                backgroundColor: neutrals.white,
                            }}
                        >
                            <Box
                                sx={{
                                    width: TIME_COLUMN_WIDTH,
                                    borderRight: `1px solid ${neutrals.gray[200]}`,
                                    backgroundColor: neutrals.gray[50],
                                }}
                            />
                            {weekDays.map((day) => {
                                const isToday = getDateKey(day) === getDateKey(today);
                                return (
                                    <Box
                                        key={day.toISOString()}
                                        sx={{
                                            textAlign: "center",
                                            padding: "6px 4px",
                                            borderLeft: `1px solid ${neutrals.gray[200]}`,
                                            backgroundColor: isToday
                                                ? `${colors.primary.main}0D`
                                                : "transparent",
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 600,
                                                color: neutrals.gray[500],
                                                textTransform: "uppercase",
                                                letterSpacing: "0.04em",
                                            }}
                                        >
                                            {formatWeekdayLabel(day)}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                        <Box
                            sx={{
                                position: "relative",
                                display: "grid",
                                gridTemplateColumns: `${TIME_COLUMN_WIDTH}px repeat(7, minmax(0, 1fr))`,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    borderRight: `1px solid ${neutrals.gray[200]}`,
                                    backgroundColor: neutrals.gray[50],
                                }}
                            >
                                {HOURS_IN_DAY.map((hour) => (
                                    <Box
                                        key={hour}
                                        sx={{
                                            height: HOUR_ROW_HEIGHT,
                                            borderBottom: `1px solid ${neutrals.gray[200]}`,
                                            display: "flex",
                                            alignItems: "flex-start",
                                            justifyContent: "center",
                                            paddingTop: "6px",
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{ color: neutrals.gray[500], fontWeight: 600 }}
                                        >
                                            {`${String(hour).padStart(2, "0")}:00`}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                            {weekDays.map((day) => {
                                const dateKey = getDateKey(day);
                                const dayEvents = scheduledEvents.filter(
                                    (event) => event.dateKey === dateKey
                                );
                                const dayEventsWithLayout = getEventLayout(dayEvents);
                                const isToday = dateKey === getDateKey(today);
                                const now = new Date();
                                const showCurrentTimeLine = isToday;
                                const currentOffset =
                                    (now.getHours() + now.getMinutes() / 60) * HOUR_ROW_HEIGHT;

                                return (
                                    <Box
                                        key={dateKey}
                                        sx={{
                                            position: "relative",
                                            borderRight: `1px solid ${neutrals.gray[200]}`,
                                            backgroundColor: isToday
                                                ? `${colors.primary.main}05`
                                                : neutrals.white,
                                        }}
                                    >
                                        {HOURS_IN_DAY.map((hour) => (
                                            <Box
                                                key={`${dateKey}-${hour}`}
                                                sx={{
                                                    height: HOUR_ROW_HEIGHT,
                                                    borderBottom: `1px solid ${neutrals.gray[200]}`,
                                                    position: "relative",
                                                    "&::after": {
                                                        content: '""',
                                                        position: "absolute",
                                                        left: 0,
                                                        right: 0,
                                                        top: "50%",
                                                        borderTop: `1px dashed ${neutrals.gray[300]}`,
                                                    },
                                                }}
                                            />
                                        ))}
                                        {showCurrentTimeLine && (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: currentOffset,
                                                    left: 0,
                                                    right: 0,
                                                    height: 2,
                                                    background: `linear-gradient(to bottom, ${neutrals.gray[900]} 0%, ${neutrals.gray[900]} 50%, ${colors.error.main} 50%, ${colors.error.main} 100%)`,
                                                    zIndex: 3,
                                                }}
                                            />
                                        )}
                                        {dayEventsWithLayout.map((event) => {
                                            const statusStyles = getStatusStyles(event.status);
                                            const eventHour = event.startMinutes / 60;
                                            const top = eventHour * HOUR_ROW_HEIGHT + 4;
                                            const height = Math.max(
                                                (EVENT_DURATION_MINUTES / 60) * HOUR_ROW_HEIGHT - 8,
                                                54
                                            );
                                            const columnWidth =
                                                event.columnCount > 1
                                                    ? `calc((100% - 16px) / ${event.columnCount})`
                                                    : "calc(100% - 16px)";
                                            const leftOffset = `calc(8px + (${columnWidth} * ${event.columnIndex}))`;

                                            return (
                                                <Tooltip
                                                    key={event.id}
                                                    title={
                                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                                <ClientAvatar event={event} size={22} />
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                                                                >
                                                                    {event.clientName || "Cliente"}
                                                                </Typography>
                                                            </Box>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ fontWeight: 700, color: theme.palette.text.primary }}
                                                            >
                                                                {event.title}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ fontWeight: 600, color: neutrals.gray[600] }}
                                                            >
                                                                {formatTooltipDate(event.scheduledDate)}
                                                            </Typography>
                                                            {CONTENT_STATUS_BADGES[event.status] && (
                                                                <Badge
                                                                    color={CONTENT_STATUS_BADGES[event.status].color}
                                                                    sx={{ alignSelf: "flex-start" }}
                                                                >
                                                                    {CONTENT_STATUS_BADGES[event.status].label}
                                                                </Badge>
                                                            )}
                                                            <PlatformIcons platforms={event.platforms} size={18} />
                                                        </Box>
                                                    }
                                                    placement="top-start"
                                                    arrow
                                                    enterDelay={200}
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                backgroundColor: neutrals.white,
                                                                color: theme.palette.text.primary,
                                                                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
                                                                borderRadius: "12px",
                                                                padding: "10px 12px",
                                                                maxWidth: 280,
                                                                border: `1px solid ${neutrals.gray[200]}`,
                                                            },
                                                        },
                                                        arrow: {
                                                            sx: {
                                                                color: neutrals.white,
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        onClick={() => {
                                                            if (onContentClick && event.piece) {
                                                                onContentClick(event.piece, event.adaptationId);
                                                            }
                                                        }}
                                                        sx={{
                                                            top,
                                                            left: leftOffset,
                                                            width: columnWidth,
                                                            height,
                                                            padding: "6px 10px 8px 12px",
                                                            borderRadius: "8px",
                                                            backgroundColor: neutrals.white,
                                                            border: `1px solid ${neutrals.gray[200]}`,
                                                            cursor: "pointer",
                                                            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: "6px",
                                                            position: "absolute",
                                                            overflow: "hidden",
                                                            "&::before": {
                                                                content: '""',
                                                                position: "absolute",
                                                                left: 0,
                                                                top: 0,
                                                                bottom: 0,
                                                                width: "4px",
                                                                backgroundColor: statusStyles.text,
                                                            },
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "space-between",
                                                                gap: 0.5,
                                                            }}
                                                        >
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                                                                <ClientAvatar event={event} />
                                                                <PlatformIcons platforms={event.platforms} />
                                                            </Box>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    color: theme.palette.text.primary,
                                                                    whiteSpace: "nowrap",
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                {event.scheduledDate.toLocaleTimeString("es-ES", {
                                                                    hour: "2-digit",
                                                                    minute: "2-digit",
                                                                })}
                                                            </Typography>
                                                        </Box>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: theme.palette.text.primary,
                                                                fontWeight: 600,
                                                                display: "block",
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            }}
                                                        >
                                                            {event.title}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            );
                                        })}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Legend */}
            <StatusLegend sx={{ columnGap: 3, rowGap: 1, pt: "16px", flexShrink: 0 }} />

            <UnifiedModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={formattedSelectedDate}
                maxWidth="sm"
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {selectedEvents.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No hay contenidos programados para este día.
                        </Typography>
                    ) : (
                        selectedEvents.map((event) => {
                            const statusStyles = getStatusStyles(event.status);

                            return (
                                <Box
                                    key={event.id}
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        if (onContentClick && event.piece) {
                                            onContentClick(event.piece, event.adaptationId);
                                        }
                                    }}
                                    sx={{
                                        padding: "12px 14px",
                                        borderRadius: "12px",
                                        border: `1px solid ${statusStyles.border}`,
                                        backgroundColor: statusStyles.background,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        cursor: "pointer",
                                        transition: "background-color 0.2s ease",
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <ClientAvatar event={event} size={20} />
                                            <PlatformIcons platforms={event.platforms} />
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 600, color: statusStyles.text }}
                                            >
                                                {event.title}
                                            </Typography>
                                        </Box>
                                        {showClientName && event.clientName && (
                                            <Typography
                                                variant="caption"
                                                sx={{ color: neutrals.gray[600], fontWeight: 500 }}
                                            >
                                                {event.clientName}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsModalOpen(false);
                                            if (onContentClick && event.piece) {
                                                onContentClick(event.piece);
                                            }
                                        }}
                                        sx={{
                                            border: "none",
                                            backgroundColor: "transparent",
                                            color: neutrals.gray[500],
                                            fontWeight: 600,
                                            fontSize: "0.75rem",
                                            cursor: "pointer",
                                            padding: 0,
                                            "&:hover": {
                                                fontWeight: 700,
                                            },
                                        }}
                                    >
                                        Ver contenido
                                    </Box>
                                </Box>
                            );
                        })
                    )}
                    {selectedEvents.length > 0 && (
                        <StatusLegend
                            swatchSize={10}
                            sx={{ gap: 2, pt: 1, borderTop: `1px solid ${neutrals.gray[200]}` }}
                        />
                    )}
                </Box>
            </UnifiedModal>
        </Box>
    );
}
